"use client";

import * as React from "react";
import { Input } from "@shared/src/components/ui/input";
import { cn } from "@shared/src/utils/cn";

interface CustomInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: boolean;
  className?: string;
  containerClassName?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  value?: string;
  required?: boolean;
  hint?: string;
  errorMessage?: string;
}

export const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      className,
      label,
      error,
      containerClassName,
      startIcon,
      endIcon,
      value = "",
      required = false,
      hint,
      errorMessage,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);

    return (
      <div className="space-y-1.5">
        <div
          className={cn(
            "relative w-full h-[62px] rounded-2xl border border-border-tertiary bg-surfaces-bg-primary transition-all duration-200",
            error ? "border-border-error" : isFocused && "border-border-focus",
            containerClassName,
          )}
        >
          {label && (
            <label
              className={cn(
                "absolute -top-2 left-3 !text-small bg-surfaces-bg-primary px-2 transition-all duration-200",
                error ? "text-text-error" : isFocused ? "text-text-brand" : "text-text-tertiary",
              )}
            >
              {label}
              {required && <span className="text-small-2 text-text-error ml-1">*</span>}
            </label>
          )}
          <div className="flex items-center h-full px-4">
            {startIcon && <div className="text-text-primary flex items-center justify-center mr-3">{startIcon}</div>}
            <Input
              {...props}
              value={value}
              ref={ref}
              // autoFocus
              onFocus={e => {
                setIsFocused(true);
                props.onFocus?.(e);
              }}
              onBlur={e => {
                setIsFocused(false);
                props.onBlur?.(e);
              }}
              className={cn(
                "border-0 h-full bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-text-primary placeholder:text-text-tertiary/60 max-sm:placeholder:text-small max-sm:text-small px-0",
                className,
              )}
            />
            {endIcon && <div className="text-text-tertiary flex items-center justify-center ml-3">{endIcon}</div>}
          </div>
        </div>
        {errorMessage ? (
          <p className="text-small text-text-error ml-4">{errorMessage}</p>
        ) : hint ? (
          <p className="text-small text-text-tertiary ml-4">{hint}</p>
        ) : null}
      </div>
    );
  },
);

CustomInput.displayName = "CustomInput";
